/**
 * useInlineTableEditing
 *
 * Orchestrates inline cell editing for the standard list table.
 * Composes useDataGridMutation for saves, manages active cell state,
 * handles keyboard navigation (Tab/Shift+Tab/Enter), drag-to-select
 * range, and fill handle (drag-down to duplicate values).
 *
 * Does NOT depend on the DataGrid orchestrator (useDataGrid) —
 * this is a standalone hook for the lightweight standard table.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type { Table as TanstackTable } from '@tanstack/react-table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { erp } from '@/lib/erp';
import type { DocTypeSchema } from '@/lib/erp/meta';
import { useDataGridMutation } from '@/components/data-grid/useDataGridMutation';
import type {
  InlineEditingContext, ActiveCell, CellEdit,
  InlineNormalizedRect, InlineCellRangePos,
} from './types';

export interface UseInlineTableEditingOptions<T> {
  doctype: string;
  /** Set of editable fieldnames from useFieldEditability */
  editableFields: Set<string>;
  /** TanStack table instance for row/column access */
  table: TanstackTable<T>;
  /** Row ID getter (must match ListTableBody's getRowId) */
  getRowId?: (row: T) => string;
}

// ---------------------------------------------------------------------------
// Range helpers
// ---------------------------------------------------------------------------

function computeNormalizedRect(
  anchorRowIdx: number, anchorColIdx: number,
  focusRowIdx: number, focusColIdx: number,
): InlineNormalizedRect {
  return {
    minRowIdx: Math.min(anchorRowIdx, focusRowIdx),
    maxRowIdx: Math.max(anchorRowIdx, focusRowIdx),
    minColIdx: Math.min(anchorColIdx, focusColIdx),
    maxColIdx: Math.max(anchorColIdx, focusColIdx),
  };
}

// Auto-scroll near edges
const AUTO_SCROLL_THRESHOLD = 40;
const AUTO_SCROLL_SPEED = 4;

function autoScroll(clientY: number): void {
  // Find the scroll container (table wrapper with overflow-x-auto)
  const container = document.querySelector('[data-density]');
  if (!container) return;
  const rect = container.getBoundingClientRect();
  if (clientY - rect.top < AUTO_SCROLL_THRESHOLD) {
    container.scrollTop -= AUTO_SCROLL_SPEED;
  } else if (rect.bottom - clientY < AUTO_SCROLL_THRESHOLD) {
    container.scrollTop += AUTO_SCROLL_SPEED;
  }
}

// Resolve row index from cursor position using data-row-id attributes
function resolveRowFromPoint(x: number, y: number, rowNames: string[]): number | null {
  const el = document.elementFromPoint(x, y);
  if (!el) return null;
  const tr = (el as HTMLElement).closest('[data-row-id]') as HTMLElement | null;
  if (!tr) return null;
  const rowId = tr.dataset.rowId;
  if (!rowId) return null;
  const idx = rowNames.indexOf(rowId);
  return idx >= 0 ? idx : null;
}

// Resolve cell (row + column) from cursor position
function resolveCellFromPoint(x: number, y: number, rowNames: string[], colIds: string[]): { rowIdx: number; colIdx: number } | null {
  const el = document.elementFromPoint(x, y);
  if (!el) return null;
  // Find the td
  const td = (el as HTMLElement).closest('td') as HTMLElement | null;
  if (!td) return null;
  // Find the tr
  const tr = td.closest('[data-row-id]') as HTMLElement | null;
  if (!tr) return null;
  const rowId = tr.dataset.rowId;
  if (!rowId) return null;
  const rowIdx = rowNames.indexOf(rowId);
  if (rowIdx < 0) return null;

  // Find column by checking td's data-col-id attribute
  const colId = td.dataset.colId;
  if (!colId) return null;
  const colIdx = colIds.indexOf(colId);
  if (colIdx < 0) return null;

  return { rowIdx, colIdx };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useInlineTableEditing<T>({
  doctype,
  editableFields,
  table,
  getRowId = (row) => (row as Record<string, unknown>).name as string ?? String(row),
}: UseInlineTableEditingOptions<T>): InlineEditingContext {
  const [activeCell, setActiveCell] = useState<ActiveCell | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [lastEditedColumn, setLastEditedColumn] = useState<string | null>(null);
  const mutation = useDataGridMutation(doctype);
  const queryClient = useQueryClient();

  // --- Range selection state ---
  const [rangeRect, setRangeRect] = useState<InlineNormalizedRect | null>(null);

  // Fetch schema for field metadata (shares cache with useFieldEditability)
  const { data: schema } = useQuery<DocTypeSchema>({
    queryKey: ['meta', doctype],
    queryFn: () => erp.meta.getDocTypeSchema(doctype),
    enabled: !!doctype,
    staleTime: 5 * 60 * 1000,
  });

  // Build field metadata map (fieldtype + options + label)
  const { filteredEditableFields, fieldMetaMap } = useMemo(() => {
    const metaMap = new Map<string, { fieldtype: string; options?: string; label?: string }>();

    if (schema?.fields) {
      for (const field of schema.fields) {
        if (editableFields.has(field.fieldname)) {
          metaMap.set(field.fieldname, {
            fieldtype: field.fieldtype,
            options: field.options,
            label: field.label,
          });
        }
      }
    }

    return { filteredEditableFields: editableFields, fieldMetaMap: metaMap };
  }, [schema, editableFields]);

  // Ordered visible column IDs (editable only, for range indexing)
  const tableColumns = table.getVisibleLeafColumns();
  const visibleColumnIds = useMemo(() => {
    return tableColumns
      .map(c => c.id)
      .filter(id => !id.startsWith('__'));
  }, [tableColumns]);

  // Column index lookup
  const colIndexMap = useMemo(() => {
    const m = new Map<string, number>();
    visibleColumnIds.forEach((id, i) => m.set(id, i));
    return m;
  }, [visibleColumnIds]);

  // Row names in order — depend on rows array, not table ref (table is stable)
  const tableRows = table.getRowModel().rows;
  const rowNames = useMemo(() => {
    return tableRows.map(r => getRowId(r.original));
  }, [tableRows, getRowId]);

  // Get visible column IDs in order (for Tab navigation)
  const getVisibleEditableColumns = useCallback((): string[] => {
    return table
      .getVisibleLeafColumns()
      .map(c => c.id)
      .filter(id => filteredEditableFields.has(id));
  }, [table, filteredEditableFields]);

  // Select cell (blue border + fill handle) — does NOT enter edit mode
  const activateCell = useCallback((rowName: string, fieldname: string) => {
    const saveState = mutation.getCellSaveState(rowName, fieldname);
    if (saveState.status === 'error') {
      mutation.clearError(rowName, fieldname);
    }
    setActiveCell({ rowName, fieldname });
    setIsEditing(false);
    setRangeRect(null);
  }, [mutation]);

  // Select AND enter edit mode (shows input)
  const startEditing = useCallback((rowName: string, fieldname: string) => {
    const saveState = mutation.getCellSaveState(rowName, fieldname);
    if (saveState.status === 'error') {
      mutation.clearError(rowName, fieldname);
    }
    setActiveCell({ rowName, fieldname });
    setIsEditing(true);
    setRangeRect(null);
  }, [mutation]);

  const deactivateCell = useCallback(() => {
    setActiveCell(null);
    setIsEditing(false);
  }, []);

  // Tab / Shift+Tab: move to next/prev editable cell in the same row (stays in edit mode)
  const moveToNextCell = useCallback((
    rowName: string,
    fieldname: string,
    direction: 'forward' | 'backward',
  ) => {
    const editableCols = getVisibleEditableColumns();
    const currentIdx = editableCols.indexOf(fieldname);
    if (currentIdx === -1) {
      setActiveCell(null);
      setIsEditing(false);
      return;
    }

    const nextIdx = direction === 'forward' ? currentIdx + 1 : currentIdx - 1;
    if (nextIdx >= 0 && nextIdx < editableCols.length) {
      setActiveCell({ rowName, fieldname: editableCols[nextIdx] });
      // Stay in editing mode during Tab navigation
    } else {
      setActiveCell(null);
      setIsEditing(false);
    }
  }, [getVisibleEditableColumns]);

  // Wrap saveCell to track last edited column
  const saveCellAndTrack = useCallback(async (edit: CellEdit) => {
    const ok = await mutation.saveCell(edit);
    if (ok) setLastEditedColumn(edit.fieldname);
    return ok;
  }, [mutation]);

  // Enter: move to same column in next row (stays in edit mode)
  const moveDown = useCallback((rowName: string, fieldname: string) => {
    const rows = table.getRowModel().rows;
    const currentIdx = rows.findIndex(r => getRowId(r.original) === rowName);
    if (currentIdx === -1 || currentIdx >= rows.length - 1) {
      setActiveCell(null);
      setIsEditing(false);
      return;
    }
    const nextRow = rows[currentIdx + 1];
    const nextRowName = getRowId(nextRow.original);
    if (filteredEditableFields.has(fieldname)) {
      setActiveCell({ rowName: nextRowName, fieldname });
      // Stay in editing mode during Enter navigation
    } else {
      setActiveCell(null);
      setIsEditing(false);
    }
  }, [table, getRowId, filteredEditableFields]);

  // --- Range selection: isCellInRange ---
  const isCellInRange = useCallback((rowIdx: number, fieldname: string): boolean => {
    if (!rangeRect) return false;
    const colIdx = colIndexMap.get(fieldname);
    if (colIdx === undefined) return false;
    return rowIdx >= rangeRect.minRowIdx && rowIdx <= rangeRect.maxRowIdx
      && colIdx >= rangeRect.minColIdx && colIdx <= rangeRect.maxColIdx;
  }, [rangeRect, colIndexMap]);

  // --- Range selection: getCellRangePos ---
  const getCellRangePos = useCallback((rowIdx: number, fieldname: string): InlineCellRangePos | null => {
    if (!rangeRect) return null;
    const colIdx = colIndexMap.get(fieldname);
    if (colIdx === undefined) return null;
    if (rowIdx < rangeRect.minRowIdx || rowIdx > rangeRect.maxRowIdx) return null;
    if (colIdx < rangeRect.minColIdx || colIdx > rangeRect.maxColIdx) return null;
    return {
      top: rowIdx === rangeRect.minRowIdx,
      bottom: rowIdx === rangeRect.maxRowIdx,
      left: colIdx === rangeRect.minColIdx,
      right: colIdx === rangeRect.maxColIdx,
    };
  }, [rangeRect, colIndexMap]);

  // --- Drag-to-select ---
  const isDraggingRef = useRef(false);
  const dragAnchorRef = useRef<{ rowIdx: number; colIdx: number } | null>(null);
  const lastResolvedRef = useRef<{ rowIdx: number; colIdx: number } | null>(null);
  const rafId = useRef(0);
  const lastClientPos = useRef({ x: 0, y: 0 });

  // Stable refs for document listeners
  const rowNamesRef = useRef(rowNames);
  rowNamesRef.current = rowNames;
  const visColsRef = useRef(visibleColumnIds);
  visColsRef.current = visibleColumnIds;

  // Track whether mouse moved during drag (to distinguish click from drag)
  const didDragRef = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const DRAG_THRESHOLD = 5; // px — ignore tiny movements during click
  // Store mousedown cell info for single-click activation on mouseup
  const mouseDownCellRef = useRef<{ rowName: string; fieldname: string } | null>(null);

  const handleDocMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    // Only count as drag after moving beyond threshold
    if (!didDragRef.current) {
      const dx = e.clientX - dragStartPos.current.x;
      const dy = e.clientY - dragStartPos.current.y;
      if (dx * dx + dy * dy < DRAG_THRESHOLD * DRAG_THRESHOLD) return;
      didDragRef.current = true;
    }
    lastClientPos.current = { x: e.clientX, y: e.clientY };

    if (rafId.current !== 0) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = 0;
      if (!isDraggingRef.current) return;

      const { x, y } = lastClientPos.current;
      const resolved = resolveCellFromPoint(x, y, rowNamesRef.current, visColsRef.current);

      if (resolved) lastResolvedRef.current = resolved;

      const pos = resolved ?? lastResolvedRef.current;
      const anchor = dragAnchorRef.current;
      if (!pos || !anchor) return;

      // Multi-cell range
      const rect = computeNormalizedRect(anchor.rowIdx, anchor.colIdx, pos.rowIdx, pos.colIdx);
      const isSingleCell = rect.minRowIdx === rect.maxRowIdx && rect.minColIdx === rect.maxColIdx;

      if (!isSingleCell) {
        // Dragging across multiple cells — clear active cell, show range
        setActiveCell(null);
        setRangeRect(rect);
      } else {
        setRangeRect(null);
      }

      autoScroll(y);
    });
  }, []);

  const handleDocMouseUp = useCallback(() => {
    // Guard: if fill drag is active, this is a stale listener — clean up and bail
    if (isFillDraggingRef.current) {
      document.removeEventListener('mousemove', handleDocMouseMove);
      document.removeEventListener('mouseup', handleDocMouseUp);
      return;
    }

    const wasDrag = didDragRef.current;
    const cell = mouseDownCellRef.current;

    isDraggingRef.current = false;
    didDragRef.current = false;
    mouseDownCellRef.current = null;
    document.removeEventListener('mousemove', handleDocMouseMove);
    document.removeEventListener('mouseup', handleDocMouseUp);
    if (rafId.current !== 0) {
      cancelAnimationFrame(rafId.current);
      rafId.current = 0;
    }

    if (!wasDrag && cell) {
      // Single click → select cell (blue border + fill handle, no editing)
      setActiveCell({ rowName: cell.rowName, fieldname: cell.fieldname });
      setIsEditing(false);
      setRangeRect(null);
    }
    // If drag happened, range is already set — don't activate any cell
  }, [handleDocMouseMove]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleDocMouseMove);
      document.removeEventListener('mouseup', handleDocMouseUp);
      if (rafId.current !== 0) cancelAnimationFrame(rafId.current);
    };
  }, [handleDocMouseMove, handleDocMouseUp]);

  const handleCellMouseDown = useCallback((rowIdx: number, fieldname: string, rowName: string, e: React.MouseEvent) => {
    if (e.button !== 0) return; // left click only
    // Guard: if fill handle drag is active, don't start cell drag
    if (isFillDraggingRef.current) return;
    // Don't stopPropagation — let click bubble to row for slide-over

    const colIdx = colIndexMap.get(fieldname);
    if (colIdx === undefined) return;

    // Don't activate yet — wait for mouseup to distinguish click vs drag
    mouseDownCellRef.current = { rowName, fieldname };
    didDragRef.current = false;
    dragStartPos.current = { x: e.clientX, y: e.clientY };

    // Start drag tracking
    dragAnchorRef.current = { rowIdx, colIdx };
    lastResolvedRef.current = { rowIdx, colIdx };
    isDraggingRef.current = true;

    document.addEventListener('mousemove', handleDocMouseMove);
    document.addEventListener('mouseup', handleDocMouseUp);
  }, [colIndexMap, handleDocMouseMove, handleDocMouseUp]);

  // --- Fill handle drag ---
  const isFillDraggingRef = useRef(false);
  const fillSourceRef = useRef<InlineNormalizedRect | null>(null);
  const fillLastRowRef = useRef<number | null>(null);
  const fillRafId = useRef(0);
  const fillLastX = useRef(0);
  const fillLastY = useRef(0);

  // Stable refs for fill mouseup handler (avoids stale closure / listener mismatch)
  const tableRef = useRef(table);
  tableRef.current = table;
  const editableFieldsRef = useRef(editableFields);
  editableFieldsRef.current = editableFields;
  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;
  const doctypeRef = useRef(doctype);
  doctypeRef.current = doctype;
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  const handleFillDocMouseMove = useCallback((e: MouseEvent) => {
    if (!isFillDraggingRef.current) return;
    fillLastX.current = e.clientX;
    fillLastY.current = e.clientY;

    if (fillRafId.current !== 0) return;
    fillRafId.current = requestAnimationFrame(() => {
      fillRafId.current = 0;
      if (!isFillDraggingRef.current) return;

      const resolved = resolveRowFromPoint(fillLastX.current, fillLastY.current, rowNamesRef.current);
      const source = fillSourceRef.current;
      if (!source) return;

      if (resolved !== null && resolved >= source.minRowIdx) {
        fillLastRowRef.current = resolved;
      }

      const targetRow = fillLastRowRef.current;
      if (targetRow === null || targetRow <= source.maxRowIdx) {
        // Within source range — show original range only
        setRangeRect(source);
        return;
      }

      // Extend range visually for fill preview
      setRangeRect({
        ...source,
        maxRowIdx: targetRow,
      });

      autoScroll(fillLastY.current);
    });
  }, []);

  // Stable mouseup handler — uses refs so it never needs to be recreated.
  // This ensures removeEventListener always removes the same function that was added.
  const handleFillDocMouseUp = useCallback(() => {
    const source = fillSourceRef.current;
    const targetRow = fillLastRowRef.current;

    isFillDraggingRef.current = false;
    document.removeEventListener('mousemove', handleFillDocMouseMove);
    document.removeEventListener('mouseup', handleFillDocMouseUp);

    if (fillRafId.current !== 0) {
      cancelAnimationFrame(fillRafId.current);
      fillRafId.current = 0;
    }

    // Apply fill if drag extended beyond source
    if (source && targetRow !== null && targetRow > source.maxRowIdx) {
      const rows = tableRef.current.getRowModel().rows;
      const sourceRow = rows[source.maxRowIdx];
      if (!sourceRow) return;
      const sourceData = sourceRow.original as Record<string, unknown>;

      // Determine editable columns in range
      const cols = visColsRef.current;
      const editCols: string[] = [];
      for (let c = source.minColIdx; c <= source.maxColIdx; c++) {
        if (editableFieldsRef.current.has(cols[c])) editCols.push(cols[c]);
      }
      if (editCols.length === 0) return;

      // Fill rows
      let count = 0;
      const fillAsync = async () => {
        for (let r = source.maxRowIdx + 1; r <= targetRow; r++) {
          const row = rows[r];
          if (!row) continue;
          const data = row.original as Record<string, unknown>;
          const name = data.name as string;
          if (!name) continue;

          for (const colId of editCols) {
            const fillValue = sourceData[colId];
            const oldValue = data[colId];
            if (fillValue === oldValue) continue;

            const ok = await mutationRef.current.saveCell({
              doctype: doctypeRef.current,
              name,
              fieldname: colId,
              value: fillValue,
              oldValue,
            });
            if (ok) count++;
          }
        }
        if (count > 0) {
          toast(`Filled ${count} cells`);
          // Invalidate list query to refresh UI (optimistic update misses nested variant rows)
          queryClientRef.current.invalidateQueries({ queryKey: ['doctype-list', doctypeRef.current] });
        }
      };
      fillAsync();
    }
  }, [handleFillDocMouseMove]);

  // Cleanup fill listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleFillDocMouseMove);
      document.removeEventListener('mouseup', handleFillDocMouseUp);
      if (fillRafId.current !== 0) cancelAnimationFrame(fillRafId.current);
    };
  }, [handleFillDocMouseMove, handleFillDocMouseUp]);

  const handleFillMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    // Source = current range or single active cell
    let sourceRect: InlineNormalizedRect;
    if (rangeRect) {
      sourceRect = { ...rangeRect };
    } else if (activeCell) {
      const rowIdx = rowNames.indexOf(activeCell.rowName);
      const colIdx = colIndexMap.get(activeCell.fieldname);
      if (rowIdx < 0 || colIdx === undefined) return;
      sourceRect = { minRowIdx: rowIdx, maxRowIdx: rowIdx, minColIdx: colIdx, maxColIdx: colIdx };
    } else {
      return;
    }

    fillSourceRef.current = sourceRect;
    fillLastRowRef.current = sourceRect.maxRowIdx;
    isFillDraggingRef.current = true;
    fillLastX.current = e.clientX;
    fillLastY.current = e.clientY;

    document.addEventListener('mousemove', handleFillDocMouseMove);
    document.addEventListener('mouseup', handleFillDocMouseUp);
  }, [rangeRect, activeCell, rowNames, colIndexMap, handleFillDocMouseMove, handleFillDocMouseUp]);

  return {
    doctype,
    editableFields: filteredEditableFields,
    activeCell,
    isEditing,
    activateCell,
    startEditing,
    deactivateCell,
    saveCell: saveCellAndTrack,
    getCellSaveState: mutation.getCellSaveState,
    clearError: mutation.clearError,
    moveToNextCell,
    moveDown,
    fieldMetaMap,
    lastEditedColumn,
    // Range selection
    rangeRect,
    isCellInRange,
    getCellRangePos,
    handleCellMouseDown,
    handleFillMouseDown,
    visibleColumnIds,
    rowNames,
  };
}
